import { useAppDispatch, useAppSelector } from "app/hooks"
import { useEffect, useRef, useState } from "react"
import {
  deleteMeal,
  getDailyProgress,
  getMeals,
  getMonthlyProgress,
  resetDailyProgress,
  setSelectedDate,
} from "./homeSlice"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import dayjs, { Dayjs } from "dayjs"
import utc from "dayjs/plugin/utc"
import timeZone from "dayjs/plugin/timezone"
import { useFailed, useSuccess } from "features/notification/hooks"
import { DATE_PARAM_FORMAT } from "config"
import { getTimeToRefreshOnNextDay } from "utils/dateTime"
import PATHS from "router/paths"
import {
  useMealTypeQueryParams,
  useSetMealTypeParams,
} from "features/meal/hooks"

dayjs.extend(utc)
dayjs.extend(timeZone)

export const useMeals = (refreshTime?: number) => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const date = searchParams.get("date") ?? dayjs().format(DATE_PARAM_FORMAT)
  const from = dayjs(date).startOf("day").utc().toISOString()
  const to = dayjs(date).endOf("day").utc().toISOString()
  const navigate = useNavigate()

  const onDateChange = (date: Dayjs) => {
    navigate(`?date=${date.format(DATE_PARAM_FORMAT)}`)
  }

  const refreshOnNextDateTimeoutRef = useRef<any>(null)

  useEffect(() => {
    const fetchMeals = () => {
      dispatch(
        getMeals({
          from,
          to,
        }),
      )
    }

    fetchMeals()

    const timeToRefresh = getTimeToRefreshOnNextDay()

    refreshOnNextDateTimeoutRef.current = setTimeout(() => {
      fetchMeals()
    }, timeToRefresh)

    return () => {
      clearTimeout(refreshOnNextDateTimeoutRef.current)
    }
  }, [date, refreshTime])

  const getMealsLoading = useAppSelector((state) => state.home.getMealsLoading)
  const getMealsFailed = useAppSelector((state) => state.home.getMealsFailed)
  const meals = useAppSelector((state) => state.home.meals)
  const isNoMeals = useAppSelector((state) => state.home.isNoMeals)

  useFailed(getMealsFailed)

  return {
    getMealsLoading,
    getMealsFailed,
    meals,
    date: dayjs(date).startOf("day"),
    onDateChange,
    isNoMeals,
  }
}

export const useUpdateSelectedDate = () => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const searchParamDate = searchParams.get("date")
  const selectedDate = useAppSelector((state) => state.home.selectedDate)

  useEffect(() => {
    if (
      searchParamDate &&
      dayjs(searchParamDate).isValid() &&
      !dayjs(searchParamDate).isSame(selectedDate, "day")
    ) {
      dispatch(setSelectedDate(dayjs(searchParamDate)))
    }
  }, [searchParamDate])
}

export const useDailyProgress = (refreshTime?: number) => {
  const dispatch = useAppDispatch()
  const date = useAppSelector((state) => state.home.selectedDate)
  const deleteMealSuccess = useAppSelector(
    (state) => state.home.deleteMealSuccess,
  )

  useEffect(() => {
    if (date) {
      dispatch(getDailyProgress(date.format(DATE_PARAM_FORMAT)))

      const GET_DAILY_PROGRESS_FADE_OUT_ANIMATION_DURATION = 150

      setTimeout(() => {
        dispatch(resetDailyProgress())
      }, GET_DAILY_PROGRESS_FADE_OUT_ANIMATION_DURATION)
    }
  }, [date, deleteMealSuccess, refreshTime])

  const getDailyProgressLoading = useAppSelector(
    (state) => state.home.getDailyProgressLoading,
  )
  const getDailyProgressFailed = useAppSelector(
    (state) => state.home.getDailyProgressFailed,
  )
  const dailyProgress = useAppSelector((state) => state.home.dailyProgress)

  useFailed(getDailyProgressFailed)

  return {
    getDailyProgressLoading,
    getDailyProgressFailed,
    dailyProgress,
  }
}

export const useMonthlyProgress = (startDate: Dayjs, endDate: Dayjs) => {
  const dispatch = useAppDispatch()

  const from = startDate.format(DATE_PARAM_FORMAT)
  const to = endDate.format(DATE_PARAM_FORMAT)

  useEffect(() => {
    if (from && to) {
      dispatch(getMonthlyProgress({ from, to }))
    }
  }, [from, to])

  const getMonthlyProgressLoading = useAppSelector(
    (state) => state.home.getMonthlyProgressLoading,
  )
  const getMonthlyProgressFailed = useAppSelector(
    (state) => state.home.getMonthlyProgressFailed,
  )
  const monthlyProgress = useAppSelector((state) => state.home.monthlyProgress)

  useFailed(getMonthlyProgressFailed)

  return {
    getMonthlyProgressLoading,
    getMonthlyProgressFailed,
    monthlyProgress,
  }
}

export const useDeleteMeal = (callback?: () => void) => {
  const dispatch = useAppDispatch()

  const handleDeleteMeal = (mealId: string) => {
    dispatch(deleteMeal(mealId))
  }

  const deleteMealLoading = useAppSelector(
    (state) => state.home.deleteMealLoading,
  )

  const deleteMealSuccess = useAppSelector(
    (state) => state.home.deleteMealSuccess,
  )

  useSuccess(deleteMealSuccess, { message: "" }, callback)

  return {
    handleDeleteMeal,
    deleteMealLoading,
  }
}

export const useNotifyDeleteMealFailed = () => {
  const deleteMealFailed = useAppSelector(
    (state) => state.home.deleteMealFailed,
  )
  useFailed(deleteMealFailed)
}

export const useDiaryAnimationLoading = (counter: Number) => {
  const [localLoading, setLocalLoading] = useState(false)
  const localLoadingTimeout = useRef<any>()
  const mounted = useRef(false)

  useEffect(() => {
    const ANIMATION_DURATION = 150
    if (!mounted.current) {
      mounted.current = true
      return
    }

    if (counter) {
      setLocalLoading(true)
      if (localLoadingTimeout.current) {
        clearTimeout(localLoadingTimeout.current)
      }
      localLoadingTimeout.current = setTimeout(() => {
        setLocalLoading(false)
      }, ANIMATION_DURATION)
    }
  }, [counter])

  return localLoading
}

// This hook initializes the selected date on the diary page
// For the Edge case when navigation from Overview page (is refreshed and lost selected date) to Diary page
export const useInitSelectedDateOnDiaryPageOnly = () => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const dateParam = searchParams.get("date")
  const selectedDate = useAppSelector((state) => state.home.selectedDate)

  useEffect(() => {
    const hasDateParam = !!dateParam
    const isDateParamValid = dayjs(dateParam).isValid()
    const isDifferentFromSelectedDate =
      !selectedDate || !dayjs(dateParam).isSame(selectedDate, "day")
    const shouldUpdateDate =
      hasDateParam && isDateParamValid && isDifferentFromSelectedDate

    if (shouldUpdateDate) {
      dispatch(setSelectedDate(dayjs(dateParam)))
    }
  }, [])
}

export const useInitDiaryDateParamsOnUrl = () => {
  const selectedDate = useAppSelector((state) => state.home.selectedDate)
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  const currentDateOnUrl = searchParams.get("date")

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!selectedDate && location.pathname !== PATHS.app.overview) {
      if (currentDateOnUrl) {
        dispatch(setSelectedDate(dayjs(currentDateOnUrl)))
      } else {
        dispatch(setSelectedDate(dayjs()))
      }
    }
  }, [])

  const { mealTypeValue } = useSetMealTypeParams()
  const from = searchParams.get("from")

  useEffect(() => {
    const isNotOverViewPage = location.pathname !== PATHS.app.overview
    setTimeout(() => {
      if (
        selectedDate &&
        currentDateOnUrl !== selectedDate.format(DATE_PARAM_FORMAT) &&
        isNotOverViewPage
      ) {
        const params: Record<string, string> = {}
        params.date = selectedDate.format(DATE_PARAM_FORMAT)

        if (mealTypeValue) {
          params.mealType = mealTypeValue.toLowerCase()
        }

        if (from) {
          params.from = from
        }

        setSearchParams(params, { replace: true })
      }
    }, 300)
  }, [selectedDate, location.pathname, mealTypeValue])
}
